import React, { useState } from 'react';
import { Send, CheckCircle2, AlertCircle, Loader2, Mail, Phone, MapPin, ShieldCheck } from 'lucide-react';
import { legalConfig } from '../../../config/legalConfig';

export const MarketingContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Web Sitesi & Dijital Hizmet Talebi',
    message: '',
    _hp: '', // Honeypot anti-spam
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.name.trim() || formData.name.trim().length < 2) {
      setError('Lütfen adınızı ve soyadınızı giriniz.');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Lütfen geçerli bir e-posta adresi giriniz.');
      return;
    }
    if (!formData.message.trim() || formData.message.trim().length < 5) {
      setError('Lütfen mesajınızı en az 5 karakter olacak şekilde yazınız.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/marketing/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Sunucudan beklenmeyen bir yanıt alındı. Lütfen daha sonra tekrar deneyin.');
      }

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Mesajınız iletilemedi. Lütfen tekrar deneyin.');
      }

      setSuccess(data.message || 'Mesajınız başarıyla iletildi. En kısa sürede sizinle iletişime geçeceğiz.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: 'Web Sitesi & Dijital Hizmet Talebi',
        message: '',
        _hp: '',
      });
    } catch (err: any) {
      setError(err?.message || 'Mesajınız gönderilirken bir hata oluştu. Lütfen bağlantınızı kontrol edip tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="iletisim" className="py-24 sm:py-32 relative bg-slate-950 border-t border-slate-800/80 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Direct Info */}
          <div className="lg:col-span-5 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
              <Mail className="w-3.5 h-3.5" />
              <span>Bize Ulaşın</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
              Sorularınız mı var? <br className="hidden sm:inline" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">
                Ekibimizle doğrudan görüşün.
              </span>
            </h2>

            <p className="text-sm text-slate-300 leading-relaxed">
              İşletmeniz için web sitesi oluşturma, yerel AI görünürlük analizi veya kurumsal bulut altyapısı hakkında bilgi almak için formu doldurun, 15 dakika içinde geri dönüş yapalım.
            </p>

            <div className="space-y-4 pt-2 text-xs sm:text-sm">
              <div className="flex items-center gap-3.5 p-3.5 rounded-xl bg-slate-900/80 border border-slate-800 text-slate-300">
                <div className="w-9 h-9 rounded-lg bg-blue-600/10 border border-blue-500/20 text-blue-400 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-slate-400 text-xs block">E-Posta</span>
                  <a href="mailto:info@adiosoft.com" className="font-semibold text-white hover:text-blue-400 transition">
                    info@adiosoft.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3.5 p-3.5 rounded-xl bg-slate-900/80 border border-slate-800 text-slate-300">
                <div className="w-9 h-9 rounded-lg bg-emerald-600/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-slate-400 text-xs block">Telefon & WhatsApp</span>
                  <a href="tel:+905323712548" className="font-semibold text-white hover:text-emerald-400 transition">
                    0532 371 2548
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3.5 p-3.5 rounded-xl bg-slate-900/80 border border-slate-800 text-slate-300">
                <div className="w-9 h-9 rounded-lg bg-purple-600/10 border border-purple-500/20 text-purple-400 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-slate-400 text-xs block">Hukuki Altyapı</span>
                  <span className="font-semibold text-white">
                    {legalConfig.companyName} bünyesinde sunulmaktadır
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Clean, Working Form */}
          <div className="lg:col-span-7">
            <div className="p-6 sm:p-8 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-2">Mesaj Gönderin</h3>
              <p className="text-xs text-slate-400 mb-6">
                Tüm mesajlar doğrudan yönetim ekibimizin paneline anlık olarak iletilmektedir.
              </p>

              {success ? (
                <div className="p-6 rounded-2xl bg-emerald-950/40 border border-emerald-500/30 text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <h4 className="text-base font-bold text-white">Talebiniz Alındı</h4>
                  <p className="text-xs text-slate-300 max-w-md mx-auto leading-relaxed">
                    {success}
                  </p>
                  <button
                    onClick={() => setSuccess(null)}
                    className="mt-4 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 transition"
                  >
                    Yeni Mesaj Gönder
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Honeypot field (hidden from genuine users) */}
                  <input
                    type="text"
                    name="_hp"
                    value={formData._hp}
                    onChange={(e) => setFormData({ ...formData, _hp: e.target.value })}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                        Ad Soyad <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Adınız Soyadınız"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                        E-Posta <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="ornek@sirketiniz.com"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                        Telefon Numarası
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="0532 000 0000"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                        İlgilendiğiniz Ürün / Konu
                      </label>
                      <select
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-blue-500 transition"
                      >
                        <option value="Web Sitesi Oluşturucu">Web Sitesi Oluşturucu (Website Builder)</option>
                        <option value="AI Yerel Görünürlük">AI Yerel Görünürlük Analizi</option>
                        <option value="Özel Alan Adı & Barındırma">Özel Alan Adı & Barındırma</option>
                        <option value="Genel Bilgi & Destek">Genel Bilgi & Destek</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      Mesajınız <span className="text-rose-400">*</span>
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="İşletmeniz veya projeniz hakkında kısaca bilgi veriniz..."
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition resize-none"
                    />
                  </div>

                  <p className="text-[11px] text-slate-400">
                    Formu göndererek{' '}
                    <a href="/gizlilik-politikasi" className="text-blue-400 hover:underline">
                      Gizlilik Politikası
                    </a>{' '}
                    ve{' '}
                    <a href="/kvkk-aydinlatma-metni" className="text-blue-400 hover:underline">
                      KVKK Aydınlatma Metni
                    </a>
                    &apos;ni okuduğunuzu kabul edersiniz.
                  </p>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-blue-600/30 transition flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99]"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>İletiliyor...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Mesajı Gönder</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default MarketingContactSection;
